import { Mail20Regular, Person20Regular } from '@fluentui/react-icons';
import { Recipient } from '@microsoft/microsoft-graph-types';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Popup } from '../Popup/Popup';
import { Text } from '../Text/Text';
import './Email.scss';

interface EmailRecipientListProps {
  recipients: Recipient[];
  // Defines how many recipients should be visible, more can be shown
  // by clicking on "more".
  maxRecipients?: number;
}

/**
 * Used for displaying a long, expandible list of email recipients
 * in the email detail view.
 */
const EmailRecipientList: React.FC<EmailRecipientListProps> = ({
  recipients,
  maxRecipients = 10,
}) => {
  const { t } = useTranslation();
  const [showAll, setShowAll] = useState<boolean>(false);

  return (
    <span>
      {recipients.slice(0, showAll ? undefined : maxRecipients).map((recipient, index) => {
        const email = get(recipient, 'emailAddress.address');
        const name = get(recipient, 'emailAddress.name');

        return (
          <Popup
            key={`recipient_popup_${email}_${name}`}
            position="below"
            pointing
            trigger={
              <span key={index} className="recipientListItem">
                {name || email};{' '}
              </span>
            }
            content={
              <div className="flex flex-col gap-1">
                {name && name !== email && (
                  <div className="flex items-center gap-1">
                    <Person20Regular className="recipientPopupIcon" />
                    <Text>{name}</Text>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-1">
                    <Mail20Regular className="recipientPopupIcon" />
                    <Text>{email}</Text>
                  </div>
                )}
              </div>
            }
          />
        );
      })}
      {recipients.length > maxRecipients && !showAll && (
        <span
          role="button"
          tabIndex={0}
          data-testid="expandRecipientsList"
          className="expandRecipientsList"
          onClick={() => setShowAll(!showAll)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              setShowAll(!showAll);
            }
          }}
        >
          +{recipients.length - maxRecipients}{' '}
          {t('recipients_show_more', { defaultValue: 'weitere' })}
        </span>
      )}
    </span>
  );
};
export default EmailRecipientList;
